/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    fork, put, call, take, select, takeEvery,
} from 'redux-saga/effects';
import mbpLogger from 'mbp-logger';

import cartServiceClient from '../../../../../../apis/cart-service-apis/cartServiceClient';

import {
    enterpriseSubmitRecipient,
    enterpriseSubmitRecipientSuccess,
    enterpriseSubmitRecipientFailed,
    enterpriseProceedSaveRecipient,
    enterpriseSaveAddressBookSuccess,
    enterpriseSaveAddressBookFailed,
    enterpriseSaveRecipientGiftMessage,
    enterpriseQASSaveRecipient,
    enterpriseSaveAddressBook,
} from './Recipient-Actions';

import { retrieveCart } from '../../Cart/ducks/RetrieveCart/RetrieveCart-Operations';
import { prepareRetreiveCart } from '../../Cart/ducks/RetrieveCart/PrepareRetreiveCart-Operations';
import { callHandleCartFailure } from '../../Cart/Cart-Actions';

import { getOrderId, getHasQasFailed } from '../../../../Common/Common-Selectors';

import {
    updateShowLoadingMessage,
    flagOffShowLoadingMessage,
} from '../../../../Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';
import { qasAddressSearchOperations } from '../../../../Common/ducks/AddressSearchQAS';
import { billingAddressIsFormAutoFilled, updateBillingAddressForm } from '../../Payment/BillingAddress/BillingAddress-Actions';
import { processFeatureErrors } from '../../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import { clearOrderItemErrors } from '../../../../Checkout/ducks/Common/ducks/Error/Error-Actions';

import { checkJWT } from '../../../../Member/ducks/Auth/Auth-Operations';
import { getGuestAddressBookEntries, getIsAuthenticatedStatus, getProfileInfo } from '../../../../Member/ducks/Common/Common-Selectors';
import { saveGuestAddressBook } from '../../../../Member/ducks/AddressBook/AddressBook-Actions';
import { flagOffShowValidatedAddressSuggestion, resetQasAddressState } from '../../../../Common/ducks/AddressSearchQAS/AddressSearchQAS-Actions';
import { workerGetFormatQasAddress } from '../../../../Common/ducks/AddressSearchQAS/AddressSearchQAS-Operations';
// import { addressBookOperations } from '../../../../Member/ducks/AddressBook';
import { workerAddAddressBookEntry, workerEditAddressBookEntry } from '../../Member/AddressBookEntry/AddressBookEntry-Operations';
import { getBillingAddress, getHasOrderItemDigitalProducts, getOrderContainsSubscriptionItem } from '../../Cart/Cart-Selectors';
import { continueToPayment } from '../../../../Checkout/ducks/Common/Common-Selectors';
import { getFeatureFlags, getRelationshipListById } from '../../App/App-Selectors';
import { triggerInterstitialLogin } from '../../../../Checkout/ducks/Global/Global-Actions';
import { workerGreetingMessage } from '../GiftMessage/GiftMessage-Operations';
import { addGreetingCardGiftMessageCompleted } from '../GiftMessage/GiftMessage-Actions';
import { workerValidateOrder } from '../../Cart/ducks/ValidateCart/ValidateCart-Operations';
import { callToTriggerValidateOrder } from '../../Cart/ducks/ValidateCart/ValidateCart-Actions';
import { verifyAddressWithGoogleSuggestion } from '../../Search/GoogleAddressSearch/GoogleAddressSearch-Operations';
import { trackEvent } from '../../../../TagManager/ducks/TagManager/TagManager-Actions';
import { getCountryCodeFromTwoToThreeDigit, getCountryDetailsByThreeDigitCountryCode } from '../../../../Checkout/helpers/countryCode';
import { workerSubmitDeliveryTime } from '../DTW/DTW-Operations';
import { workerGetFormatQAS } from '../AddressQAS/AddressQAS-Operations';
import { flagOffShowValidatedQAS, setUnverifiedFlowActive } from '../AddressQAS/AddressQAS-Actions';
import { qasAddressOperations } from '../AddressQAS';

function checkIfSavingInternationalProduct(recipientAddress) {
    if (recipientAddress.zipCode === '00000') {
        return true;
    }
    return false;
}

const getAddressOneLineString = (address) => {
    const buildAddress = `${address.firstName?.trim()} ${address.lastName?.trim()}, ${address.locationType?.trim()} ${address.organizationName?.trim()}, ${address.address1?.trim()} ${address.address2?.trim()}, ${address.city?.trim()} ${address.state?.trim()} ${address.country?.trim()}, ${address.zipCode?.trim()}, ${(address.phone?.trim() || address.phoneNumber?.trim())}`;
    return buildAddress?.replace(/ ,/g, ',')?.trim();
};

const normailizeArrayToObject = (arr) => {
    const normalize = {};
    arr.forEach((item) => {
        normalize[[Object.keys(item)]] = item[[Object.keys(item)]];
    });
    return normalize;
};

const countryISOAlfa2Code = {
    USA: 'US',
    CAN: 'CA',
};

function* enterpriseSubmitRecipientForm(action) {
    try {
        // Show Site Loader
        yield put(updateShowLoadingMessage());

        const orderId = yield select(getOrderId);

        // Clear Errors recipient and gift message
        yield put(clearOrderItemErrors(
            action.data.orderItemId,
            'recipient',
            { errType: 'clearAllFields', field: '' },
        ));

        yield put(clearOrderItemErrors(
            action.data.orderItemId,
            'giftMessage',
            { errType: 'clearAllFields', field: '' },
        ));

        yield put(clearOrderItemErrors(
            orderId,
            'cart',
            { errType: 'clearAllFields', field: '' },
        ));

        yield put(clearOrderItemErrors(
            orderId,
            'payment',
            { errType: 'clearAllFields', field: '' },
        ));

        const {
            dpvIndicator, isAddressVerified, isDigitalProduct,
            orderItemId, pageType, recipient, useAddressForAll,
            qasLocationType, cartLocationType, cartZipCode, itemDTWData,
        } = action.data;

        // Recipient Form Data
        let recipientFormDetails = recipient;

        const isAuthenticated = yield select(getIsAuthenticatedStatus);
        const isInternational = yield call(checkIfSavingInternationalProduct, recipientFormDetails);
        const featureFlags = yield select(getFeatureFlags);
        const hasOrderItemDigitalProducts = yield select(getHasOrderItemDigitalProducts);

        const relationShipList = yield select(getRelationshipListById);

        const isOccasionRecipientEnabled = featureFlags['is-recipient-occasion-enabled'];
        const isRelationshipRecipientEnabled = featureFlags['is-relationship-type-enabled'];

        if (isAddressVerified) {
            recipientFormDetails.addressStatus = isAddressVerified;
            recipientFormDetails.isAddressVerified = false;
            if (['Y', 'V'].includes(isAddressVerified)) {
                recipientFormDetails.addressStatus = 'V';
                recipientFormDetails.isAddressVerified = true;
            } else if (!['USA', 'CAN'].includes(recipientFormDetails.country)) {
                recipientFormDetails.addressStatus = 'O';
            }
        }

        if (dpvIndicator) {
            recipientFormDetails.dpvIndicator = dpvIndicator;
        }

        if (recipientFormDetails.addressId) {
            recipientFormDetails.isAddressUpdate = true;
        }

        if (isDigitalProduct || isInternational) {
            recipientFormDetails.skipAddressBookAdd = true;
        }

        let isUseAddressForAll = useAddressForAll;
        if (hasOrderItemDigitalProducts && isUseAddressForAll) {
            isUseAddressForAll = false;
        }

        if (recipientFormDetails.country?.length === 2) {
            const country = getCountryCodeFromTwoToThreeDigit(recipientFormDetails.country || 'US');
            recipientFormDetails.country = country;
        }

        const isQasFailed = yield select(getHasQasFailed);

        // QAS enabled and failed or when google address search is enabled
        if ((featureFlags['is-qas-address-verification-enabled'] && isQasFailed) || featureFlags['is-google-places-enabled']) {
            recipientFormDetails = yield call(verifyAddressWithGoogleSuggestion, recipientFormDetails);
        }

        let relationship = recipient.relationship || null;
        if (relationship && relationShipList[relationship]) {
            relationship = relationShipList[relationship];
        }

        delete recipientFormDetails.relationship;

        let occasions = [];
        const isNewRemindersEnabled = featureFlags['is-new-reminders-enabled'];
        if (isOccasionRecipientEnabled && recipient.occasions?.length > 0) {
            occasions = [...recipient.occasions];
            if (
                (!isNewRemindersEnabled && recipient.occasions[0].occasionType === 'Event')
                || (isNewRemindersEnabled && occasions[0].occasionMonth)
            ) {
                occasions[0].occasionMonth = (`0${String(recipient.occasions[0].occasionMonth)}`).slice(-2) || '';

                occasions[0].occasionDay = (`0${String(recipient.occasions[0].occasionDay)}`).slice(-2) || '';

                if (recipient.occasions[0].occasionYear) {
                    occasions[0].occasionYear = String(recipient.occasions[0].occasionYear) || '';
                }

                // Extra occasion birthday
                if (recipient.occasions.length > 1) {
                    occasions[1].occasionMonth = (`0${String(recipient.occasions[1].occasionMonth)}`).slice(-2) || '';

                    occasions[1].occasionDay = (`0${String(recipient.occasions[1].occasionDay)}`).slice(-2) || '';

                    if (recipient.occasions[1].occasionYear) {
                        occasions[1].occasionYear = String(recipient.occasions[1].occasionYear) || '';
                    }
                }
            }
        }
        delete recipientFormDetails.occasions;

        const jwtToken = yield call(checkJWT);
        const cartId = yield select(getOrderId);

        const reqObj = {
            env: {},
            jwtToken,
            cartId,
            cartItemId: orderItemId,
            useAddressForAll: isUseAddressForAll,
            recipient: recipientFormDetails,
            occasions,
            relationship,
        };

        if (qasLocationType) {
            reqObj.recipient.attributes = [
                {
                    attributeName: 'qasLocationType',
                    attributeValue: qasLocationType,
                },
            ];
        }

        // Save Recipient Address
        yield call(cartServiceClient.saveRecipient, reqObj);

        if (isRelationshipRecipientEnabled && relationship) {
            yield put(trackEvent({
                eventCategory: 'Checkout',
                eventAction: 'Feature Interaction',
                eventLabel: `Relationship Capture | ${relationship}`,
            }));
        }

        if (isOccasionRecipientEnabled && occasions?.length > 0) {
            yield put(trackEvent({
                eventCategory: 'Checkout',
                eventAction: 'Feature Interaction',
                eventLabel: `Occasion Capture | ${occasions[0].occasionName}`,
            }));

            if (occasions?.length > 1) {
                yield put(trackEvent({
                    eventCategory: 'Checkout',
                    eventAction: 'Feature Interaction',
                    eventLabel: 'Occasion Capture | Second Birthday Reminder Open',
                }));
            }
        }

        // the location type change on recipient then DTW need be clean up to standard
        if (
            (
                (
                    cartLocationType
                    && cartLocationType !== recipientFormDetails.locationType
                )
                || (
                    cartZipCode
                    && cartZipCode !== recipientFormDetails.zipCode
                )
            )
            && itemDTWData
            && itemDTWData.dtwId !== 'DDW'
        ) {
            const reqObjDTWStandard = {
                data: {
                    dtwId: 'DDW',
                    amount: '0.0',
                    name: 'Standard Delivery',
                    orderItemId,
                    deliveryDate: itemDTWData.deliveryDate,
                    pageType,
                    reason: 'updateRecipient',
                },
            };
            yield call(workerSubmitDeliveryTime, reqObjDTWStandard);
        }

        // If QAS has failed for this recipient wee reset it,
        // for the user can try  one more time on the next product.
        const hasQASFailed = yield select(getHasQasFailed);

        if (hasQASFailed) {
            yield put(setUnverifiedFlowActive(false));
            yield put(resetQasAddressState());
        }

        // Avoid saving recipient address to guest address book if product is international
        // or if is NBCU
        if (!isAuthenticated && !isInternational && !isDigitalProduct) {
            const getGuestAddressBook = yield select(getGuestAddressBookEntries);

            let filteredAddressBook = [];
            if (getGuestAddressBook && getGuestAddressBook.length > 0) {
                const currentRecipient = getAddressOneLineString(recipientFormDetails);

                filteredAddressBook = getGuestAddressBook.filter((addressObject) => {
                    const addressBookRecipient = getAddressOneLineString(addressObject);
                    return addressBookRecipient === currentRecipient;
                });
            }
            if (filteredAddressBook?.length === 0) {
                const recipientDetails = { ...recipientFormDetails };
                if (recipientFormDetails.phoneNumber) {
                    recipientDetails.phone = recipientFormDetails.phoneNumber;
                }
                yield put(saveGuestAddressBook(recipientDetails));
            }
        }

        if (action.data.checkSameAsDeliveryAddress) {
            const userProfile = yield select(getProfileInfo);
            const billingAddress = yield select(getBillingAddress);
            if (isAuthenticated) {
                reqObj.recipient.email = userProfile.email;
                reqObj.recipient.confirmEmail = userProfile.email;
            }

            if (billingAddress.phone && reqObj.recipient.phone === '') {
                reqObj.recipient.phone = billingAddress.phone;
            }

            if (recipientFormDetails.country?.length === 3) {
                const countryObj = getCountryDetailsByThreeDigitCountryCode(recipientFormDetails.country || 'US');
                reqObj.recipient.country = countryObj.countryCodeTwoDigit;
            }

            const billingInfo = yield select(getBillingAddress);

            const billingData = {
                firstName: reqObj.recipient.firstName,
                lastName: reqObj.recipient.lastName,
                address1: reqObj.recipient.address1,
                address2: reqObj.recipient.address2,
                organizationName: reqObj.recipient.organizationName,
                locationType: reqObj.recipient.locationType,
                city: reqObj.recipient.city,
                state: reqObj.recipient.state,
                zipCode: reqObj.recipient.zipCode,
                country: reqObj.recipient.country,
                phone: billingInfo.phone,
                email: billingInfo.email,
            };

            yield put(updateBillingAddressForm(billingData));
            yield put(billingAddressIsFormAutoFilled(true));
        }

        if (action.data.handleOpenRecipientForm) {
            yield call(action.data.handleOpenRecipientForm, false);
        }

        yield put(enterpriseSubmitRecipientSuccess());

        // Prepare Retreive cart
        yield call(prepareRetreiveCart, pageType);

        // Get Cart
        yield call(retrieveCart);

        yield put(flagOffShowLoadingMessage());
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'Recipient-Operations.js',
            function: 'enterpriseSubmitRecipientForm',
            message: 'Universal: Error saving recipient page',
            jsError: ex,
        });

        yield put(callHandleCartFailure(ex));

        ex.orderItemId = action.data.orderItemId;

        yield fork(processFeatureErrors, {
            data: {
                orderItemId: action.data.orderItemId,
                errorFeatureType: 'recipient',
                errorValues: ex.response?.data,
            },
        });

        yield put(enterpriseSubmitRecipientFailed());
        yield put(flagOffShowLoadingMessage());
    }
}

function* watcherProcessEnterpriseSubmitRecipient() {
    yield takeEvery(enterpriseSubmitRecipient().type, enterpriseSubmitRecipientForm);
}

function* workerEnterpriseProceedSaveRecipient(action) {
    const featureFlags = yield select(getFeatureFlags);

    let proceedToSave = null;
    if (featureFlags['is-checkout-using-qas-v2']) {
        proceedToSave = yield call(qasAddressOperations.workers.workerValidateAddressQAS, action);
    } else {
        proceedToSave = yield call(qasAddressSearchOperations.workers.validateAddressQAS, action);
    }

    const { allowSave, addressVerifiedStatus, qasLocationType } = proceedToSave;

    if (allowSave) {
        const updateVerification = { ...action };
        updateVerification.data.dpvIndicator = addressVerifiedStatus.dpvIndicator;
        updateVerification.data.isAddressVerified = addressVerifiedStatus.isAddressVerified;
        updateVerification.qasLocationType = qasLocationType;

        yield call(enterpriseSubmitRecipientForm, updateVerification);
    }
}

function* watcherEnterpriseProceedSaveRecipient() {
    yield takeEvery(enterpriseProceedSaveRecipient().type, workerEnterpriseProceedSaveRecipient);
}

function* updateEnterpriseSaveAddressBook(action) {
    try {
        if (action.data.typeQAS === 'qasAddressSelection') {
            yield put(updateShowLoadingMessage());
        }

        const recipientData = action.data.recipient;
        const orderId = yield select(getOrderId);
        // Clear Errors recipient and gift message
        yield put(clearOrderItemErrors(
            action.data.orderItemId,
            'recipient',
            { errType: 'clearAllFields', field: '' },
        ));

        yield put(clearOrderItemErrors(
            action.data.orderItemId,
            'giftMessage',
            { errType: 'clearAllFields', field: '' },
        ));

        yield put(clearOrderItemErrors(
            orderId,
            'cart',
            { errType: 'clearAllFields', field: '' },
        ));
        yield put(clearOrderItemErrors(
            orderId,
            'payment',
            { errType: 'clearAllFields', field: '' },
        ));

        const {
            firstName, lastName, organizationName, address1, address2, city, state,
            zipCode, country, phoneNumber, email, locationType, relationship, addressBookRecipientId,
        } = recipientData;

        let formRecipientValues = {
            firstName,
            lastName,
            addressBookRecipientId,
            employerName: organizationName || '',
            addressLineOne: address1 || '',
            addressLineTwo: address2 || '',
            city,
            state,
            zip: zipCode,
            country,
            phoneNumber,
            email: email || '',
            isBillingAddress: false,
            addressType: locationType,
            relationship: relationship || '',
            orderItemId: action.data.orderItemId,
            addressStatus: action.data.isAddressVerified,
            dpvIndicator: action.data.dpvIndicator,
        };

        let recipientFormDetails = { ...recipientData };

        if (action.data.typeQAS === 'qasAddressSelection') {
            yield put(resetQasAddressState());

            const featureFlags = yield select(getFeatureFlags);
            let getFormatQASAction = workerGetFormatQasAddress;
            if (featureFlags['is-checkout-using-qas-v2']) {
                getFormatQASAction = workerGetFormatQAS;
            }

            const responseGetFormatQAS = yield call(getFormatQASAction, { data: { address: action.data.addressQASSelected } });
            const formatAddress = responseGetFormatQAS.result?.data || responseGetFormatQAS.data?.result || {};

            const { components, address, metadata } = formatAddress;

            let qasAddress1 = null;
            let qasAddress2 = null;
            let deliveryType = null;

            if (featureFlags['is-checkout-using-qas-v2']) {
                qasAddress1 = address.address_line_1;
                qasAddress2 = `${address.address_line_2} ${address.address_line_3}`.trim();
                deliveryType = metadata?.address_classification?.delivery_type;

                if (['APO', 'FPO'].includes(components.locality?.town.name)) {
                    qasAddress1 = components.street.full_name;
                    qasAddress2 = components.secondary_delivery_service.full_name;
                }

                recipientFormDetails = {
                    ...recipientData,
                    address1: qasAddress1 || '',
                    address2: qasAddress2 || '',
                    city: address.locality,
                    state: address.region,
                    zip: components.postal_code.primary,
                    country: components.country_iso_3,
                };

                formRecipientValues = {
                    ...formRecipientValues,
                    addressLineOne: qasAddress1 || '',
                    addressLineTwo: qasAddress2 || '',
                    city: address.locality,
                    state: address.region,
                    zip: components.postal_code.primary,
                    country: components.country_iso_3,
                };
            } else {
                const nComponents = normailizeArrayToObject(components);
                const nAddress = normailizeArrayToObject(address);

                qasAddress1 = nAddress.addressLine1;
                qasAddress2 = `${nAddress.addressLine2} ${nAddress.addressLine3}`?.trim();
                deliveryType = metadata?.addressClassification?.deliveryType;

                if (['APO', 'FPO'].includes(nComponents.locality1)) {
                    qasAddress1 = nComponents.street1;
                    qasAddress2 = nComponents.deliveryService4;
                }

                // We need populate the data address qas in the form.
                recipientFormDetails = {
                    ...recipientData,
                    address1: qasAddress1 || '',
                    address2: qasAddress2 || '',
                    city: nAddress.locality,
                    state: nAddress.province,
                    zip: nAddress.postalCode,
                    zipCode: nAddress.postalCode,
                    country: countryISOAlfa2Code[nComponents.countryISO1],
                };

                formRecipientValues = {
                    ...formRecipientValues,
                    addressLineOne: qasAddress1 || '',
                    addressLineTwo: qasAddress2 || '',
                    city: nAddress.locality,
                    state: nAddress.province,
                    zip: nAddress.postalCode,
                    zipCode: nAddress.postalCode,
                    country: countryISOAlfa2Code[nComponents.countryISO1],
                };
            }

            if (deliveryType) {
                recipientFormDetails.attributes = [
                    {
                        attributeName: 'qasLocationType',
                        attributeValue: deliveryType,
                    },
                ];
            }
        }

        if (recipientData.addressId) {
            formRecipientValues.addressBookEntryId = recipientData.addressId;
            yield call(workerEditAddressBookEntry, { data: formRecipientValues, cartItemId: action.data.orderItemId });
            if (action.data.handleCallBack) {
                yield call(action.data.handleCallBack, false, recipientFormDetails, 'addressBook');
            }
        } else {
            yield call(workerAddAddressBookEntry, {
                data: formRecipientValues,
                handleCallBack: action.data?.handleCallBack,
                recipientFormDetails,
                cartItemId: action.data.orderItemId,
            });
        }

        yield put(enterpriseSaveAddressBookSuccess());
        yield put(flagOffShowLoadingMessage());
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'Recipient-Operations.js',
            function: 'updateEnterpriseSaveAddressBook',
            message: 'Error updating Enterprise Save Address Book',
            jsError: ex,
        });

        yield put(callHandleCartFailure(ex));

        yield put(enterpriseSaveAddressBookFailed());
        yield put(flagOffShowLoadingMessage());
    }
}

function* watcherEnterpriseSaveAddressBook() {
    yield takeEvery(enterpriseSaveAddressBook().type, updateEnterpriseSaveAddressBook);
}

function* workerSaveRecipientGiftMessage(action) {
    let recipientFormDetails = {};

    try {
        const jwtToken = yield call(checkJWT);
        const cartId = yield select(getOrderId);
        const relationShipList = yield select(getRelationshipListById);

        // Show Site Loader
        yield put(updateShowLoadingMessage());

        const {
            dpvIndicator, isAddressVerified, isDigitalProduct,
            history, orderItemId, pageType, recipient, useAddressForAll,
            qasLocationType, cartLocationType, cartZipCode, itemDTWData,
        } = action.data;

        yield put(clearOrderItemErrors(
            cartId,
            'payment',
            { errType: 'clearAllFields', field: '' },
        ));

        yield put(clearOrderItemErrors(
            orderItemId,
            'recipient',
            { errType: 'clearAllFields', field: '' },
        ));

        // Recipient Form Data
        const featureFlags = yield select(getFeatureFlags);

        recipientFormDetails = { ...recipient };

        const isAuthenticated = yield select(getIsAuthenticatedStatus);
        const isInternational = yield call(checkIfSavingInternationalProduct, recipientFormDetails);
        const isOccasionRecipientEnabled = featureFlags['is-recipient-occasion-enabled'];
        const isRelationshipRecipientEnabled = featureFlags['is-relationship-type-enabled'];

        if (isAddressVerified) {
            recipientFormDetails.addressStatus = isAddressVerified;
            recipientFormDetails.isAddressVerified = false;
            if (['Y', 'V'].includes(isAddressVerified)) {
                recipientFormDetails.addressStatus = 'V';
                recipientFormDetails.isAddressVerified = true;
            } else if (!['USA', 'CAN'].includes(recipientFormDetails.country)) {
                recipientFormDetails.addressStatus = 'O';
            }
        }

        if (dpvIndicator) {
            recipientFormDetails.dpvIndicator = dpvIndicator;
        }

        if (recipientFormDetails.addressId) {
            recipientFormDetails.isAddressUpdate = true;
        } else {
            recipientFormDetails.isAddressUpdate = false;
        }

        if (isDigitalProduct || isInternational) {
            recipientFormDetails.skipAddressBookAdd = true;
        }

        let relationshipValue = null;

        if (recipient.relationship && relationShipList[recipient.relationship]) {
            relationshipValue = relationShipList[recipient.relationship];
        }

        delete recipientFormDetails.relationship;

        let occasions = [];
        const isNewRemindersEnabled = featureFlags['is-new-reminders-enabled'];

        if (isOccasionRecipientEnabled && recipient.occasions?.length > 0) {
            occasions = [...recipient.occasions];

            if (
                (!isNewRemindersEnabled && recipient.occasions[0].occasionType === 'Event')
                || (isNewRemindersEnabled && occasions[0].occasionMonth)
            ) {
                occasions[0].occasionMonth = (`0${String(recipient.occasions[0].occasionMonth)}`).slice(-2) || '';

                occasions[0].occasionDay = (`0${String(recipient.occasions[0].occasionDay)}`).slice(-2) || '';

                if (recipient.occasions[0].occasionYear) {
                    occasions[0].occasionYear = String(recipient.occasions[0].occasionYear) || '';
                }

                // Extra occasion birthday
                if (recipient.occasions.length > 1) {
                    occasions[1].occasionMonth = (`0${String(recipient.occasions[1].occasionMonth)}`).slice(-2) || '';

                    occasions[1].occasionDay = (`0${String(recipient.occasions[1].occasionDay)}`).slice(-2) || '';

                    if (recipient.occasions[1].occasionYear) {
                        occasions[1].occasionYear = String(recipient.occasions[1].occasionYear) || '';
                    }
                }
            }
        }

        delete recipientFormDetails.occasions;

        // When the user select the address from QAS
        if (action.data.typeQAS === 'qasAddressSelection') {
            yield put(resetQasAddressState());

            let getFormatQASAction = workerGetFormatQasAddress;
            if (featureFlags['is-checkout-using-qas-v2']) {
                getFormatQASAction = workerGetFormatQAS;
            }

            const responseGetFormatQAS = yield call(getFormatQASAction, { data: { address: action.data.addressQASSelected } });
            const formatAddress = responseGetFormatQAS.result?.data || responseGetFormatQAS.data?.result || {};

            const { components, address, metadata } = formatAddress;

            let qasAddress1 = null;
            let qasAddress2 = null;
            let deliveryType = null;

            if (featureFlags['is-checkout-using-qas-v2']) {
                qasAddress1 = address.address_line_1;
                qasAddress2 = `${address.address_line_2} ${address.address_line_3}`.trim();
                deliveryType = metadata?.address_classification?.delivery_type;
                if (['APO', 'FPO'].includes(components.locality?.town.name)) {
                    qasAddress1 = components.street.full_name;
                    qasAddress2 = components.secondary_delivery_service.full_name;
                }
                recipientFormDetails = {
                    ...recipientFormDetails,
                    address1: qasAddress1 || '',
                    address2: qasAddress2 || '',
                    city: address.locality,
                    state: address.region,
                    country: components.country_iso_3,
                    zip: components.postal_code.primary,
                    zipCode: components.postal_code.primary,
                };
            } else {
                const nComponents = normailizeArrayToObject(components);
                const nAddress = normailizeArrayToObject(address);

                qasAddress1 = nAddress.addressLine1;
                qasAddress2 = `${nAddress.addressLine2} ${nAddress.addressLine3}`?.trim();
                deliveryType = metadata?.addressClassification?.deliveryType;

                if (['APO', 'FPO'].includes(nComponents.locality1)) {
                    qasAddress1 = nComponents.street1;
                    qasAddress2 = nComponents.deliveryService4;
                }

                // We need populate the data address qas in the form.
                recipientFormDetails = {
                    ...recipientFormDetails,
                    address1: qasAddress1 || '',
                    address2: qasAddress2 || '',
                    city: nAddress.locality,
                    state: nAddress.province,
                    zip: nAddress.postalCode,
                    zipCode: nAddress.postalCode,
                    country: countryISOAlfa2Code[nComponents.countryISO1],
                };
            }

            if (deliveryType) {
                recipientFormDetails.attributes = [
                    {
                        attributeName: 'qasLocationType',
                        attributeValue: deliveryType,
                    },
                ];
            }
        }

        if (recipientFormDetails.country?.length === 2) {
            const country = getCountryCodeFromTwoToThreeDigit(recipientFormDetails.country || 'US');
            recipientFormDetails.country = country;
        }

        const isQasFailed = yield select(getHasQasFailed);

        // QAS enabled and failed or when google address search is enabled
        if ((featureFlags['is-qas-address-verification-enabled'] && isQasFailed) || featureFlags['is-google-places-enabled']) {
            recipientFormDetails = yield call(verifyAddressWithGoogleSuggestion, recipientFormDetails);
        }

        const reqObj = {
            env: {},
            jwtToken,
            cartId,
            cartItemId: orderItemId,
            useAddressForAll,
            recipient: recipientFormDetails,
            occasions,
            relationship: relationshipValue,
        };

        if (qasLocationType) {
            reqObj.recipient.attributes = [
                {
                    attributeName: 'qasLocationType',
                    attributeValue: qasLocationType,
                },
            ];
        }

        // Save Recipient Address
        yield call(cartServiceClient.saveRecipient, reqObj);

        if (isRelationshipRecipientEnabled && relationshipValue) {
            yield put(trackEvent({
                eventCategory: 'Checkout',
                eventAction: 'Feature Interaction',
                eventLabel: `Relationship Capture | ${relationshipValue}`,
            }));
        }

        if (isOccasionRecipientEnabled && occasions?.length > 0) {
            yield put(trackEvent({
                eventCategory: 'Checkout',
                eventAction: 'Feature Interaction',
                eventLabel: `Occasion Capture | ${occasions[0].occasionName}`,
            }));

            if (occasions?.length > 1) {
                yield put(trackEvent({
                    eventCategory: 'Checkout',
                    eventAction: 'Feature Interaction',
                    eventLabel: 'Occasion Capture | Second Birthday Reminder Open',
                }));
            }
        }

        // the location type change on recipient then DTW need be clean up to standard
        if (
            (
                (
                    cartLocationType
                    && cartLocationType !== recipientFormDetails.locationType
                )
                || (
                    cartZipCode
                    && cartZipCode !== recipientFormDetails.zipCode
                )
            )
            && itemDTWData
            && itemDTWData.dtwId !== 'DDW'
        ) {
            const reqObjDTWStandard = {
                data: {
                    dtwId: 'DDW',
                    amount: '0.0',
                    name: 'Standard Delivery',
                    orderItemId,
                    deliveryDate: itemDTWData.deliveryDate,
                    pageType,
                    reason: 'updateRecipient',
                },
            };
            yield call(workerSubmitDeliveryTime, reqObjDTWStandard);
        }

        // Avoid saving recipient address to guest address book if product is international
        // or if is NBCU
        if (!isAuthenticated && !isInternational && !isDigitalProduct) {
            const getGuestAddressBook = yield select(getGuestAddressBookEntries);
            let filteredAddressBook = [];
            if (getGuestAddressBook && getGuestAddressBook.length > 0) {
                const currentRecipient = getAddressOneLineString(recipientFormDetails);

                filteredAddressBook = getGuestAddressBook.filter((addressObject) => {
                    const addressBookRecipient = getAddressOneLineString(addressObject);
                    return addressBookRecipient === currentRecipient;
                });
            }

            if (filteredAddressBook?.length === 0) {
                const recipientDetails = { ...recipientFormDetails };

                if (recipientFormDetails.phoneNumber) {
                    recipientDetails.phone = recipientFormDetails.phoneNumber;
                }
                yield put(saveGuestAddressBook(recipientDetails));
            }
        }

        if (action.data.checkSameAsDeliveryAddress) {
            const userProfile = yield select(getProfileInfo);
            if (isAuthenticated) {
                reqObj.recipient.email = userProfile.email;
                reqObj.recipient.confirmEmail = userProfile.email;
            }

            if (recipientFormDetails.phoneNumber && reqObj.recipient.phone === '') {
                reqObj.recipient.phone = recipientFormDetails.phoneNumber;
            }

            if (recipientFormDetails.country?.length === 3) {
                const countryObj = getCountryDetailsByThreeDigitCountryCode(recipientFormDetails.country || 'US');
                reqObj.recipient.country = countryObj.countryCodeTwoDigit;
            }

            const billingInfo = yield select(getBillingAddress);
            const billingData = {
                firstName: reqObj.recipient.firstName,
                lastName: reqObj.recipient.lastName,
                address1: reqObj.recipient.address1,
                address2: reqObj.recipient.address2,
                organizationName: reqObj.recipient.organizationName,
                locationType: reqObj.recipient.locationType,
                city: reqObj.recipient.city,
                state: reqObj.recipient.state,
                zipCode: reqObj.recipient.zipCode,
                country: reqObj.recipient.country,
                phone: billingInfo.phone,
                email: billingInfo.email,
            };

            yield put(updateBillingAddressForm(billingData));
            yield put(billingAddressIsFormAutoFilled(true));
        }

        // If QAS has failed for this recipient wee reset it,
        // for the user can try  one more time on the next product.
        const hasQASFailed = yield select(getHasQasFailed);

        if (hasQASFailed) {
            yield put(setUnverifiedFlowActive(false));
            yield put(resetQasAddressState());
        }

        // if giftmessage data is present then we save gift message
        // otherwise we skip it.
        if (action.data?.greetingData?.type) {
            // Add Greeting Cards or GiftMessage
            yield fork(workerGreetingMessage, action);
            yield take(addGreetingCardGiftMessageCompleted().type);
        }

        // Prepare Retreive cart
        yield call(prepareRetreiveCart, pageType);
        yield call(retrieveCart);

        // Close the QAS modal.
        yield put(flagOffShowValidatedAddressSuggestion());
        yield put(flagOffShowValidatedQAS());

        if (action.data?.pageToRedirect === 'payment') {
            // User is Remembered ?
            const userProfile = yield select(getProfileInfo);
            const orderContainsSubscriptionItem = yield select(getOrderContainsSubscriptionItem);
            const isContinueToPayment = yield select(continueToPayment);

            let isRememberedUser = false;
            if (userProfile.email) {
                isRememberedUser = true;
            }

            // TODO: migrate the workerValidateOrder inside enterprise +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
            // it is looking the other recipient operations, doesn look correct
            if (orderContainsSubscriptionItem.length > 0 && featureFlags['is-subscription-interstitial-login-enabled'] && !isAuthenticated) {
                yield put(triggerInterstitialLogin(history, true));
            } else if ((featureFlags['is-interstitial-login-enabled'] || featureFlags['is-native-interstitial-page-enabled']) && (!isAuthenticated && !isRememberedUser)) {
                yield put(triggerInterstitialLogin(history));
            } else if (isContinueToPayment) {
                yield fork(workerValidateOrder, { history: action.data.history });
                yield take(callToTriggerValidateOrder().type);
            }
        } else if (action.data?.handleCallBack) {
            if (action.data?.addressQASSelected?.suggestion || action.data?.addressQASSelected?.text || action.data?.typeQAS === 'keep') {
                yield call(action.data.handleCallBack, false, recipientFormDetails);
            } else {
                yield call(action.data.handleCallBack, false);
            }
        }

        yield put(flagOffShowLoadingMessage());
    } catch (ex) {
        console.log('ex', ex);
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'Recipient-Operations.js',
            function: 'workerSaveRecipientGiftMessage',
            message: 'Error saving recipient and gift message process',
            jsError: ex,
        });

        if (action.data && action.data.handleCallBack && (action.data.addressQASSelected?.suggestion || action.data.addressQASSelected?.text || action.data.typeQAS === 'keep')) {
            yield call(action.data.handleCallBack, false, recipientFormDetails, null, true);
        }
        yield put(callHandleCartFailure(ex));

        ex.orderItemId = action.data.orderItemId;

        yield fork(processFeatureErrors, {
            data: {
                orderItemId: action.data.orderItemId,
                errorFeatureType: 'recipient',
                errorValues: ex.response?.data,
            },
        });

        // Close the QAS modal if it is open
        yield put(flagOffShowValidatedAddressSuggestion());
        yield put(flagOffShowValidatedQAS());

        yield put(enterpriseSubmitRecipientFailed());
        yield put(flagOffShowLoadingMessage());
    }
}

function* watcherSaveRecipientGiftMessage() {
    yield takeEvery(enterpriseSaveRecipientGiftMessage().type, workerSaveRecipientGiftMessage);
}

function* qasSaveRecipient(action) {
    const featureFlags = yield select(getFeatureFlags);
    let proceedToSave = null;
    if (featureFlags['is-checkout-using-qas-v2']) {
        proceedToSave = yield call(qasAddressOperations.workers.workerValidateAddressQAS, action);
    } else {
        proceedToSave = yield call(qasAddressSearchOperations.workers.validateAddressQAS, action);
    }

    const { allowSave, addressVerifiedStatus, qasLocationType } = proceedToSave;
    if (allowSave) {
        const updateVerification = { ...action };
        updateVerification.data.dpvIndicator = addressVerifiedStatus.dpvIndicator;
        updateVerification.data.isAddressVerified = addressVerifiedStatus.isAddressVerified;
        updateVerification.data.qasLocationType = qasLocationType;

        if (action.data.parentType === 'addressbook') {
            const reqObj = {
                data: {
                    recipient: {
                        ...action.data.recipient,
                        qasLocationType,
                    },
                    typeQAS: '',
                    addressQASSelected: {},
                    parentType: action.data.parentType,
                    handleCallBack: action.data.handleCallBack,
                    orderItemId: action?.data?.orderItemId,
                    orderItemIndex: action.data.orderItemIndex,
                    dpvIndicator: action.data.dpvIndicator,
                    isAddressVerified: action.data.isAddressVerified,
                    cartLocationType: action.data.locationType,
                    cartZipCode: action.data.zipCode,
                    itemDTWData: action.data.itemDTWData,
                },
            };
            yield fork(updateEnterpriseSaveAddressBook, reqObj);
            yield take(enterpriseSaveAddressBookSuccess().type);

            // Close addressbook and qas
            yield call(action.data.handleCallBack);
        } else {
            yield call(workerSaveRecipientGiftMessage, { data: updateVerification.data });
        }
    }
}

function* watcherEnterpriseQASSaveRecipient() {
    yield takeEvery(enterpriseQASSaveRecipient().type, qasSaveRecipient);
}

const watchers = [
    fork(watcherProcessEnterpriseSubmitRecipient),
    fork(watcherEnterpriseProceedSaveRecipient),
    fork(watcherEnterpriseQASSaveRecipient),
    fork(watcherEnterpriseSaveAddressBook),
    fork(watcherSaveRecipientGiftMessage),
];

export { watchers };

export default {};
